import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "react-bootstrap";

import Button from "components/form/Button";
import Dropdown from "components/form/Dropdown";
import CalendarFilterDropdown from "components/Layout/CalendarFilterDropdown";
import Card from "components/Layout/Card";
import Loader from "components/Layout/Loader";

import {
  actionOnReportedPost,
  reportedPost,
  setPostID,
  setPostRemoved,
  showSuccess,
} from "store/slices";
import { icons, REPORT_REASONS } from "utils/constants";
import { objectToFormData } from "utils/helpers";

const ReportRequestList = () => {
  const dispatch = useDispatch();
  const { postCategoryList, postRemoved } = useSelector((state) => state.global);
  const { userDetails } = useSelector((state) => state.student);

  const { update: updateAccess, delete: deleteAccess } =
    userDetails?.role_data?.find((item) =>
      ["Network Management", "My Post and Networks"].includes(item?.permission_name)
    )?.sub_permission || {};

  const [postCategory, setPostCategory] = useState("");
  const [searchObject, setSearchObject] = useState({ date_range: "Last 24 Hours" });
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [loadingPostId, setLoadingPostId] = useState(null);
  const [timer, setTimer] = useState(null);

  const [paginate, setPaginate] = useState({
    offset: 0,
    limit: 9,
    total: 0,
  });

  const getReportedPost = useCallback(
    async (params = {}) => {
      try {
        setIsLoading(true);

        const payload = {
          date_range: params.date_range || searchObject.date_range,
          category: params.category || postCategory,
          offset: params.offset ?? paginate.offset,
          limit: params.limit ?? paginate.limit,
        };

        const response = await dispatch(reportedPost(objectToFormData(payload)));
        const data = response?.data?.reported_posts || [];

        const formattedData = data.map((item) => ({
          id: item.post_id,
          title: item.post_title || "Untitled Post",
          reports: item.reports.map((r) => {
            const matched = REPORT_REASONS.find(
              (reason) =>
                String(reason.value) === String(r.reason) ||
                reason.label.toLowerCase() === r.reason.toLowerCase()
            );
            return {
              reason: matched ? matched.label : r.reason,
              count: r.count,
            };
          }),
          totalReports: item.total_reports,
        }));

        setReportData(formattedData);
        setPaginate((prev) => ({
          ...prev,
          offset: payload.offset,
          limit: payload.limit,
          total: response?.data?.count || 0,
        }));
      } catch (error) {
        console.error("❌ Error fetching reported posts:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, postCategory, searchObject, paginate.offset, paginate.limit]
  );

  const handleDebouncedSearch = useCallback(
    (params) => {
      if (timer) clearTimeout(timer);
      const newTimer = setTimeout(() => getReportedPost(params), 600);
      setTimer(newTimer);
    },
    [getReportedPost, timer]
  );

  useEffect(() => {
    getReportedPost(searchObject);
    return () => timer && clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (postRemoved) {
      getReportedPost({
        ...searchObject,
        offset: paginate.offset,
        limit: paginate.limit,
        category: postCategory,
      });
      dispatch(setPostRemoved(false));
    }
  }, [postRemoved]);

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setPostCategory(category);
    handleDebouncedSearch({ category });
  };

  const handleDateChange = (e) => {
    const range = e.target.value;
    const newSearch = { ...searchObject, date_range: range };
    setSearchObject(newSearch);
    handleDebouncedSearch(newSearch);
  };

  const handleRemovePost = async (postId) => {
    try {
      setLoadingPostId(postId);
      const payload = { post_id: postId, action: "reject" };

      const response = await dispatch(actionOnReportedPost(objectToFormData(payload)));
      if (response?.data) {
        dispatch(showSuccess(response?.message));
        getReportedPost(searchObject);
      }
    } catch (error) {
      console.error("❌ Error removing post:", error);
    } finally {
      setLoadingPostId(null);
    }
  };

  const handleViewPost = (postId) => {
    dispatch(setPostID(postId));
  };

  const activePage = paginate.offset / paginate.limit + 1;
  const totalPage = Math.ceil(paginate.total / paginate.limit);

  const handlePagination = (offset) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    getReportedPost({ offset });
  };

  // ------------------------------------------------------------------

  return (
    <div className="cmb-20">

      <div className="d-flex justify-content-between align-items-center">
        <div className="text-16-500-20 color-title-navy">
          Report Request ({reportData?.length || 0})
        </div>

        <div className="d-flex align-items-center gap-3">
          <Dropdown
            id="category"
            placeholder="All Category"
            value={postCategory}
            options={postCategoryList}
            optionValue="name"
            onChange={handleCategoryChange}
            className="small-dropdown"
          />
          <CalendarFilterDropdown onChange={handleDateChange} elem={{ title: "Join Date" }} />
        </div>
      </div>

      {isLoading && <Loader />}

      <div className="row g-3 cmt-7">
        {!isLoading && reportData.length === 0 && (
          <div className="text-center text-muted">No reports found</div>
        )}

        {reportData.map((post) => (
          <div key={post.id} className="col-12 col-md-6 col-lg-4">
            <Card className="cp-20 border shadow-sm" style={{ minHeight: "304px" }}>
              {/* Title */}
              <div
                className="text-14-500 color-new-car cmb-14"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {post.title}
              </div>

              {/* Reports */}
              <div className="cmb-14">
                {post.reports.map((report, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center mb-1 text-14-400 color-black-olive"
                  >
                    <span className="me-2">•</span>
                    <span className="me-1">{report.reason}</span>
                    <span>({report.count})</span>
                  </div>
                ))}
              </div>

              {/* Total Reports */}
              <div className="text-14-500 color-5110">
                {post.totalReports} Report{post.totalReports > 1 ? "s" : ""} of this post
              </div>

              {/* Actions */}
              <div className="d-flex flex-row gap-2 cmt-14">
                {deleteAccess === "1" && (
                  <Button
                    text={loadingPostId === post.id ? "Removing..." : "Remove Post"}
                    btnStyle="primary-dark"
                    className="h-35 cps-20 cpe-20"
                    onClick={() => handleRemovePost(post.id)}
                    disabled={loadingPostId === post.id}
                    btnLoading={loadingPostId === post.id}
                  />
                )}

                <Button
                  text="View Post"
                  icon={
                    <span className="cpe-5">
                      <img src={icons.DarkBlueEye} loading="lazy" alt="view" />
                    </span>
                  }
                  btnStyle="primary-light-outline"
                  className="h-35 cps-20 cpe-20"
                  onClick={() => handleViewPost(post.id)}
                />
              </div>
            </Card>
          </div>
        ))}
      </div>

      {/* 🔹 Pagination */}
      {totalPage > 1 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap footer-block mt-3">
          <div className="d-flex gap-4">
            <Button
              icon={<i className="bi bi-chevron-left me-1" />}
              text="Previous"
              btnStyle={activePage === 1 ? "primary-gray" : "primary-dark"}
              className="cps-20 cpe-20"
              onClick={() => activePage > 1 && handlePagination(paginate.offset - paginate.limit)}
              isRounded
            />
            <Button
              rightIcon={<i className="bi bi-chevron-right ms-2" />}
              text="Next"
              btnStyle={activePage === totalPage ? "primary-gray" : "primary-dark"}
              className="cps-40 cpe-30"
              onClick={() =>
                activePage < totalPage && handlePagination(paginate.offset + paginate.limit)
              }
              isRounded
            />
          </div>

          <Pagination className="d-flex align-items-center m-0">
            <span className="text-16-400 color-davys-gray me-1">Page</span>
            <Pagination.Item disabled>{activePage}</Pagination.Item>
            <span className="text-16-400 color-davys-gray mx-2">of</span>
            <Pagination.Item disabled>{totalPage}</Pagination.Item>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default ReportRequestList;
